import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { isVideoFile, isYouTubeUrl, parseYouTubeId } from "../utils/mediaUtils";

const SWIPE_THRESHOLD = 50;
// 以當前張為中心，預先渲染（＝載入）的前後張數。
// 相簿可能有數百張，不一次全抓，只抓附近幾張讓左右切換時已在快取中。
const PRELOAD_BEHIND = 1;
const PRELOAD_AHEAD = 4;

const isMediaVideo = (src) => isYouTubeUrl(src) || isVideoFile(src);

/**
 * 相簿燈箱：點開相簿後顯示大圖，可左右切換、鍵盤 ← → / ESC、手機左右滑動。
 */
export default function AlbumLightbox({
  open,
  items,
  index,
  onIndexChange,
  onClose,
  title,
  description,
}) {
  const list = items || [];
  const total = list.length;
  const current = list[index] || list[0];
  const hasPrev = index > 0;
  const hasNext = index < total - 1;

  // 已載入完成的圖片；當前張未載入完成時顯示載入特效
  const [loaded, setLoaded] = useState(() => new Set());
  const markLoaded = (src) =>
    setLoaded((s) => (s.has(src) ? s : new Set(s).add(src)));

  // 鍵盤：ESC 關、← → 切換
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && hasPrev) onIndexChange(index - 1);
      else if (e.key === "ArrowRight" && hasNext) onIndexChange(index + 1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, index, hasPrev, hasNext, onClose, onIndexChange]);

  // 鎖背景捲動
  useEffect(() => {
    if (!open) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  // 手機滑動
  const touchStartX = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > SWIPE_THRESHOLD) {
      if (delta > 0 && hasPrev) onIndexChange(index - 1);
      else if (delta < 0 && hasNext) onIndexChange(index + 1);
    }
    touchStartX.current = null;
  };

  if (!open) return null;

  const currentSrc = current?.image || "";
  const currentIsVideo = isMediaVideo(currentSrc);
  const showSpinner = !current || (!currentIsVideo && !loaded.has(currentSrc));

  // 要渲染的圖片：當前張＋前後幾張（影片只在當前張時才渲染，避免預載整支影片）
  const from = Math.max(0, index - PRELOAD_BEHIND);
  const to = Math.min(list.length - 1, index + PRELOAD_AHEAD);
  const windowItems = [];
  for (let i = from; i <= to; i += 1) {
    const src = list[i]?.image;
    if (src && !isMediaVideo(src)) windowItems.push({ i, src });
  }

  const caption = current?.title || title;
  const captionDesc = current?.description || description;

  const overlay = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[9999] flex flex-col bg-black/95"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* 頂部：張數 + 關閉 */}
      <div className="flex items-center justify-between px-4 py-4 text-white/95 md:px-6">
        <span className="font-mono text-sm tracking-[0.25em] opacity-75">
          {total > 0 && `${String(index + 1).padStart(2, "0")} / ${String(total).padStart(2, "0")}`}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="關閉"
          className="rounded-full p-2 transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M6 6l12 12M6 18L18 6" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* 中間：媒體 + 左右箭頭 */}
      <div
        className="relative flex flex-1 items-center justify-center overflow-hidden px-2 md:px-16"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        {hasPrev && (
          <button
            type="button"
            onClick={() => onIndexChange(index - 1)}
            aria-label="上一張"
            className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 mobile:hidden"
          >
            <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        <div className="relative h-full w-full max-w-[1400px]">
          {/* 圖片：當前張與附近幾張疊在一起，只顯示當前張；其餘先載入快取，切換時立即出現 */}
          {windowItems.map(({ i, src }) => (
            <img
              key={src + i}
              src={src}
              alt={i === index ? caption || "" : ""}
              aria-hidden={i !== index}
              draggable={false}
              decoding="async"
              onLoad={() => markLoaded(src)}
              className={`absolute inset-0 m-auto h-full w-full select-none object-contain transition-opacity duration-300 ${
                i === index && loaded.has(src) ? "opacity-100" : "pointer-events-none opacity-0"
              }`}
            />
          ))}

          {/* 影片 / YouTube：只渲染當前張 */}
          {currentIsVideo && (
            <div className="absolute inset-0 flex items-center justify-center">
              {isYouTubeUrl(currentSrc) ? (
                <iframe
                  key={currentSrc}
                  title={caption || "YouTube 影片"}
                  src={`https://www.youtube.com/embed/${parseYouTubeId(currentSrc) || ""}?rel=0&playsinline=1`}
                  className="aspect-video max-h-full w-full max-w-[1100px]"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <video
                  key={currentSrc}
                  src={currentSrc}
                  className="max-h-full max-w-full"
                  controls
                  playsInline
                  preload="metadata"
                >
                  您的瀏覽器不支援影片播放。
                </video>
              )}
            </div>
          )}

          {/* 載入特效：當前張圖片尚未載入完成 */}
          {showSpinner && (
            <div
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
              role="status"
              aria-label="載入中"
            >
              <span className="h-9 w-9 animate-spin rounded-full border-2 border-white/20 border-t-white/70" />
            </div>
          )}
        </div>

        {hasNext && (
          <button
            type="button"
            onClick={() => onIndexChange(index + 1)}
            aria-label="下一張"
            className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 mobile:hidden"
          >
            <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
      </div>

      {/* 底部：說明 + 手機版左右按鈕 */}
      <div className="mx-auto w-full max-w-4xl px-6 py-5 text-center text-white md:px-10">
        {caption && (
          <h3 className="mb-2 text-xl font-semibold mobile:text-lg">{caption}</h3>
        )}
        {captionDesc && (
          <p className="mx-auto max-w-2xl text-base leading-relaxed opacity-85 mobile:line-clamp-3 mobile:text-sm">
            {captionDesc}
          </p>
        )}

        <div className="mt-5 hidden items-center justify-center gap-3 mobile:flex">
          <button
            type="button"
            onClick={() => hasPrev && onIndexChange(index - 1)}
            disabled={!hasPrev}
            aria-label="上一張"
            className="rounded-full bg-white/10 p-3 transition-colors hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-25"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="px-2 text-xs tracking-wider opacity-60">滑動切換</span>
          <button
            type="button"
            onClick={() => hasNext && onIndexChange(index + 1)}
            disabled={!hasNext}
            aria-label="下一張"
            className="rounded-full bg-white/10 p-3 transition-colors hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-25"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  // Portal 掛到 body，避免被頁面上層 transform / overflow 影響定位
  return createPortal(overlay, document.body);
}

AlbumLightbox.propTypes = {
  open: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
    })
  ),
  index: PropTypes.number.isRequired,
  onIndexChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
};
